import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { applyTopK, rankGalleryDecisions } from '../nistMatchRank';

function formatScore(value, modality) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'N/A';
  if (modality === 'IRIS') {
    return `${(Number(value) * 100).toFixed(1)}%`;
  }
  return Number(value).toFixed(4);
}

export default function NistOneToNResults({
  decisions = [],
  modality = 'FACE',
  topK,
  onTopKChange,
  galleryFileNames = [],
  title = '1:N ranked gallery',
}) {
  const ranked = rankGalleryDecisions(decisions, modality);
  const visible = applyTopK(ranked, topK);

  return (
    <Card className="bg-gray-50">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-4">
        <CardTitle className="text-lg">{title}</CardTitle>
        <div className="flex items-center gap-2">
          <Label htmlFor="nistTopK" className="text-sm text-gray-600 whitespace-nowrap">
            Top-K
          </Label>
          <input
            id="nistTopK"
            type="number"
            min={1}
            max={Math.max(1, ranked.length)}
            value={topK}
            onChange={(e) => onTopKChange?.(Number(e.target.value))}
            className="w-16 rounded border px-2 py-1 text-sm"
          />
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600 border-b">
                <th className="py-1 pr-2">Rank</th>
                <th className="py-1 pr-2">Gallery #</th>
                <th className="py-1 pr-2">File</th>
                <th className="py-1 text-right">Score</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row, rankIdx) => (
                <tr key={`${row.galleryIndex}-${rankIdx}`} className="border-b border-gray-100">
                  <td className="py-1 pr-2">{rankIdx + 1}</td>
                  <td className="py-1 pr-2">{row.galleryIndex}</td>
                  <td className="py-1 pr-2 truncate max-w-[12rem]">
                    {galleryFileNames[row.galleryIndex] || `gallery-${row.galleryIndex}`}
                  </td>
                  <td className="py-1 text-right font-medium">{formatScore(row.score, modality)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {ranked.length > visible.length && (
          <p className="text-xs text-gray-500 mt-2">
            Showing top {visible.length} of {ranked.length} candidates
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function NistMxNMatrixResults({ matrix = [], galleryFileNames = [], modality = 'FACE' }) {
  if (!matrix.length) return null;

  const formatCell = (score) => {
    if (score == null || Number.isNaN(Number(score))) return '—';
    return modality === 'IRIS' ? `${(Number(score) * 100).toFixed(0)}%` : Number(score).toFixed(3);
  };

  const galleryCount = matrix[0]?.decisions?.length || 0;

  return (
    <Card className="bg-gray-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Table 6 M×N match matrix</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left p-1">Probe</th>
              {Array.from({ length: galleryCount }, (_, gi) => (
                <th key={gi} className="p-1 text-center">
                  G{gi}
                  <div className="font-normal text-gray-500 truncate max-w-[4rem]">
                    {galleryFileNames[gi] || ''}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((row) => (
              <tr key={row.probeIndex}>
                <td className="p-1 font-medium">P{row.probeIndex}</td>
                {row.decisions.map((cell, gi) => (
                  <td key={gi} className="p-1 text-center">
                    {formatCell(cell.score)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
